import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle, WarningCircle, XCircle, ArrowsClockwise, DownloadSimple } from "@phosphor-icons/react";
import { relativeTime } from "@/lib/utils";

/**
 * Ingestion-health tile for the Admin Dashboard. Compares Microsoft Graph's
 * live mailbox count with the tracker's tombstone count and surfaces the
 * "Last poll" timestamp so admins spot a stalled scheduler at a glance.
 *
 * When drift is >5% the "Backfill last 7 days" button becomes primary — one
 * click walks every mailbox message in the last week and ingests anything
 * we haven't seen. All existing dedup guards (case-ref, identity,
 * conversation-id) still apply so re-runs are safe and idempotent.
 */
export default function SyncHealthTile({ isAdmin }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sync/health");
      setHealth(data);
    } catch (e) {
      setHealth(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/sync/health");
        if (!cancelled) setHealth(data);
      } catch {}
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const backfill = async () => {
    if (!window.confirm("Backfill the last 7 days of mailbox messages? Safe to re-run — dedup will skip anything already ingested.")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/sync/backfill", null, { params: { days: 7 } });
      toast.success(`Backfill complete: scanned ${data.polled} messages, ${data.new_leads} new lead${data.new_leads === 1 ? "" : "s"} created, ${data.merged_into_existing} merged.`, { duration: 12000 });
      await fetchHealth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  if (!isAdmin) return null;

  const status = (() => {
    if (loading || !health) return { color: "slate", icon: ArrowsClockwise, label: "Checking…" };
    if (!health.graph_ok) return { color: "rose", icon: XCircle, label: "Graph unreachable" };
    if (!health.in_sync) return { color: "amber", icon: WarningCircle, label: `${health.drift} unseen` };
    return { color: "emerald", icon: CheckCircle, label: "In sync" };
  })();
  const StatusIcon = status.icon;
  const chipCls = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-900 border-amber-300",
    rose: "bg-rose-50 text-rose-800 border-rose-200",
  }[status.color];

  return (
    <div data-testid="sync-health-tile" className="bg-white border border-slate-200 rounded-sm p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="label-tracking">Ingestion</div>
          <h3 className="font-display text-xl text-slate-900 mt-1">Mailbox sync</h3>
        </div>
        <span
          data-testid="sync-status-chip"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-sm text-xs font-medium ${chipCls}`}
        >
          <StatusIcon size={14} weight="fill" />
          {status.label}
        </span>
      </div>

      <dl className="mt-5 grid grid-cols-3 gap-4 text-sm">
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-slate-500">Mailbox</dt>
          <dd className="font-mono text-2xl text-slate-900 mt-1" data-testid="mailbox-count">
            {health?.mailbox_count ?? "—"}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-slate-500">Seen</dt>
          <dd className="font-mono text-2xl text-slate-900 mt-1" data-testid="tombstone-count">
            {health?.tombstone_count ?? "—"}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-slate-500">Tracked</dt>
          <dd className="font-mono text-2xl text-slate-900 mt-1" data-testid="leads-count">
            {health?.leads_count ?? "—"}
          </dd>
        </div>
      </dl>

      <p className="mt-4 text-xs text-slate-500">
        Last poll{" "}
        <span className="text-slate-800" data-testid="last-poll">
          {health?.last_poll_at ? relativeTime(health.last_poll_at) : "never"}
        </span>
      </p>

      {health?.graph_error && (
        <p className="mt-2 text-[11px] text-rose-700 font-mono break-all" data-testid="graph-error">
          {health.graph_error}
        </p>
      )}

      <div className="mt-5 flex items-center gap-2">
        <Button
          data-testid="backfill-btn"
          onClick={backfill}
          disabled={backfilling || !health?.graph_ok}
          size="sm"
          className={`rounded-sm gap-2 ${status.color === "amber" ? "bg-slate-900 hover:bg-slate-800" : ""}`}
          variant={status.color === "amber" ? "default" : "outline"}
        >
          <DownloadSimple size={14} className={backfilling ? "animate-pulse" : ""} />
          {backfilling ? "Backfilling…" : "Backfill last 7 days"}
        </Button>
        <button
          data-testid="refresh-health-btn"
          onClick={fetchHealth}
          className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
        >
          <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>
    </div>
  );
}
